import React, { useCallback, useEffect, useState } from "react";
import { ContentType } from "@types";
import styles from "./selectedContent.module.scss";
import FullScreenWrapper from "../full-screen-wrapper/FullScreenWrapper";
import Video from "./video/Video";
import Audio from "./audio/Audio";
import Slide from "./slide/Slide";
import RevealSlideView from "./revealslide/Reveal";
// import GoogleSlide from './google-slide/GoogleSlide';
import Form from "./form/Form";
import Pdf from "./pdf/Pdf";
import DefaultContent from "./Default";
import { useNextContent } from "../useNextContent";
import preview from "../../../assets/img/preview.svg?url";
import {
  SllModule,
  SllLesson,
  useSllUpdateLastVisitedModuleMutation,
} from "@api/graphql-codegen";
import { useIntercom } from "react-use-intercom";
import { useStandaloneContent } from "@src/hooks/useStandaloneContent";
import { useMediaQuery } from "react-responsive";
import { useActivityTracker } from "@src/hooks/useActivity";
import MaterialPDF from "@src/components/material-pdf/MaterialPDF";
import ZipRevealSlideView from "./revealslide/ZipReveal";
import { logger } from '../../../utils/logger';

type Props = {
  content: SllModule;
  lesson?: SllLesson;
  lessons?: SllLesson[];
  goBack?: () => void;
  onExit?: () => void;
  isPreview?: boolean;
  showBanner?: boolean; // Preview banner
  lastPageNum?: number;
  isParentApp?: boolean;
  studyPlanId?: string;
  startLesson?: (lesson: SllLesson) => void;
  isMaterial?: boolean;
  needPrint?: boolean;
};

function SelectedContent(props: Props) {
  const {
    content,
    lesson,
    goBack,
    isPreview = false,
    showBanner = false,
    lastPageNum,
    studyPlanId,
    isParentApp,
    startLesson,
    isMaterial = false,
    needPrint = false,
  } = props;
  const { hasNextContent, nextContent, selectNext } = useNextContent();
  const { pageNumber, setPageNumber, setIsPreview } = useStandaloneContent();
  const [curContent, setCurContent] = useState<SllModule>();
  const [timer, setTimer] = useState<number>(30 * 60); // 30 minutes in seconds
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { update } = useIntercom();
  const isPhone = useMediaQuery({ maxWidth: 600 });
  const { pauseActivityTracker, resumeActivityTracker } = useActivityTracker();

  useEffect(() => {
    update?.({
      hideDefaultLauncher: true,
    });
    if (isPreview) {
      setIsPreview(true);
    }
    // return () => update?.({
    //   'hideDefaultLauncher': false
    // })
  }, [isPreview, setIsPreview, update]);

  useEffect(() => {
    if (lastPageNum) {
      setPageNumber(lastPageNum);
    }
  }, [lastPageNum, setPageNumber]);

  const [sllUpdateLastVisitedModule] = useSllUpdateLastVisitedModuleMutation();

  useEffect(() => {
    if (curContent?.id && curContent?.id !== content.id) {
      setPageNumber(1);
    }
    setCurContent(content);
  }, [lastPageNum, curContent?.id, content, setCurContent, setPageNumber]);

  useEffect(() => {
    if (
      !isParentApp &&
      !isPreview &&
      !studyPlanId &&
      lesson?.id &&
      content.id
    ) {
      const curIndex =
        lesson.presentation?.findIndex((m) => m?.id === content.id) || 0;
      const prevModule =
        curIndex >= 1 ? lesson.presentation?.at(curIndex - 1) : undefined;
      sllUpdateLastVisitedModule({
        variables: {
          input: {
            lessonId: lesson.id,
            moduleId: content.id,
            previousModuleId: prevModule?.id,
            pageNumber: pageNumber,
          },
        },
      });
    }
  }, [
    lesson,
    content.id,
    sllUpdateLastVisitedModule,
    pageNumber,
    isParentApp,
    isPreview,
    studyPlanId,
  ]);

  useEffect(() => {
    if (!isPreview) {
      // Pause activity tracker when a teacher starts a lesson
      pauseActivityTracker();
    }

    // Resume activity tracker when a teacher ends a lesson
    return () => resumeActivityTracker();
  }, [isPreview, pauseActivityTracker, resumeActivityTracker]);

  useEffect(() => {
    if (timer > 0) {
      const timerId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timerId); // Clear the interval on component unmount
    }
  }, [timer]);

  useEffect(() => {
    if (timer === 0) {
      resumeActivityTracker();
    }
  }, [resumeActivityTracker, timer]);

  const onNext = useCallback(() => {
    if (hasNextContent && !nextContent?.pauseHere) {
      selectNext(content);
    }
  }, [content, hasNextContent, nextContent?.pauseHere, selectNext]);

  const handlePageNumber = useCallback(
    (value: any) => {
      setPageNumber(value);
    },
    [setPageNumber]
  );

  const handleStartLesson = useCallback(() => {
    if (startLesson && lesson) startLesson(lesson);
  }, [startLesson, lesson]);

  const handleFullscreen = () => {
    setIsFullscreen((current) => !current);
  };

  const presentationContent = useCallback(
    (isFullScreen) => {
      if (
        content &&
        (content.data ||
          content.formData ||
          content.type === ContentType.PARENT)
      ) {
        switch (content.type) {
          case ContentType.VIDEO: {
            return (
              <Video
                data={content.data!}
                key={content.id}
                isFullScreen={isFullScreen}
                metaData={content}
                onNext={onNext}
                isPreview={isPreview}
                goBack={goBack}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.AUDIO: {
            return (
              <Audio
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.PDF: {
            if (isMaterial) {
              return (
                <MaterialPDF
                  needPrint={needPrint}
                  data={content.data!}
                  isFullScreen={isFullScreen}
                  goBack={goBack}
                  goFullscreen={handleFullscreen}
                />
              );
            } else {
              return (
                <Pdf
                  data={content.data!}
                  isFullScreen={isFullScreen}
                  isPreview={isPreview}
                  goBack={goBack}
                />
              );
            }
          }
          case ContentType.SLIDE: {
            return (
              <Slide
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.REVEALSLIDE: {
            return (
              <RevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.ZIPSLIDE: {
            return (
              <ZipRevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.FORM: {
            return (
              <Form
                data={content.data!}
                formData={content.formData}
                isFullScreen={isFullScreen}
                id={content.id}
                name={content.name}
                isPreview={isPreview}
                goBack={goBack}
                studyPlanId={studyPlanId}
                lesson={lesson}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          default: {
            logger.debug(`[Not supported type]: `, content.type);
          }
        }
      } else if (content) {
        return (
          <DefaultContent
            isFullScreen={isFullScreen}
            isPreview={isPreview}
            content={content}
            goBack={goBack}
          />
        );
      }
    },
    [
      content,
      onNext,
      isPreview,
      goBack,
      pageNumber,
      handlePageNumber,
      studyPlanId,
      lesson,
      handleStartLesson,
      isMaterial,
      needPrint,
    ]
  );

  return (
    <>
      {isPreview ? (
        <div className={styles.wrapper}>
          {showBanner && (
            <div className={styles.previewBanner}>
              <img
                src={preview}
                alt={"preview"}
                style={{ width: isPhone ? "90px" : "" }}
              />
            </div>
          )}
          <div
            className={isPreview ? styles.contentWithPreview : styles.content}
            style={{ marginTop: 10 }}
          >
            {presentationContent(false)}
          </div>
        </div>
      ) : (
        <FullScreenWrapper lesson={lesson} content={content} goBack={goBack} isMaterial={isMaterial} isFullscreen={isFullscreen}>
          {presentationContent}
        </FullScreenWrapper>
      )}
    </>
  );
}

export default SelectedContent;
